
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PortalFilters from './components/PortalFilters';
import ListingCard from './components/ListingCard';
import InquiryModal from './components/InquiryModal';
import { updateSEO, generateMarketplaceSchema } from '../../utils/seo';

interface Listing {
  id: string;
  description: string;
  mrr: number;
  totalRevenue: number;
  cashFlow: number;
  location: string;
  niches: string[];
  employeeCount: number;
  askingPrice: number;
  type: 'agency' | 'saas' | 'ecommerce';
  featured: boolean;
}

const mockListings: Listing[] = [
  {
    id: '1',
    description: 'Full-service digital marketing agency specializing in performance marketing for e-commerce brands. Proven track record with 50+ successful campaigns.',
    mrr: 45000,
    totalRevenue: 540000,
    cashFlow: 162000,
    location: 'Austin, TX',
    niches: ['E-commerce', 'Performance Marketing', 'Paid Ads'],
    employeeCount: 4,
    askingPrice: 1620000,
    type: 'agency',
    featured: true
  },
  {
    id: '2',
    description: 'Boutique creative agency focused on brand identity and web design for tech startups. Strong portfolio with Series A+ companies.',
    mrr: 28000,
    totalRevenue: 336000,
    cashFlow: 100800,
    location: 'San Francisco, CA',
    niches: ['Brand Design', 'Web Development', 'Tech Startups'],
    employeeCount: 3,
    askingPrice: 1008000,
    type: 'agency',
    featured: false
  },
  {
    id: '3',
    description: 'SEO and content marketing agency with proprietary tools and processes. Serving B2B SaaS companies with proven ROI metrics.',
    mrr: 35000,
    totalRevenue: 420000,
    cashFlow: 126000,
    location: 'Denver, CO',
    niches: ['SEO', 'Content Marketing', 'B2B SaaS'],
    employeeCount: 5,
    askingPrice: 1260000,
    type: 'agency',
    featured: true
  },
  {
    id: '4',
    description: 'Social media management agency specializing in influencer partnerships and community building for lifestyle brands.',
    mrr: 22000,
    totalRevenue: 264000,
    cashFlow: 79200,
    location: 'Miami, FL',
    niches: ['Social Media', 'Influencer Marketing', 'Lifestyle Brands'],
    employeeCount: 3,
    askingPrice: 792000,
    type: 'agency',
    featured: false
  },
  {
    id: '5',
    description: 'Email marketing automation agency with advanced segmentation strategies. Focus on e-commerce and subscription businesses.',
    mrr: 18000,
    totalRevenue: 216000,
    cashFlow: 64800,
    location: 'Portland, OR',
    niches: ['Email Marketing', 'Marketing Automation', 'E-commerce'],
    employeeCount: 2,
    askingPrice: 648000,
    type: 'agency',
    featured: false
  },
  {
    id: '6',
    description: 'Video production and motion graphics agency serving tech companies and startups. Award-winning creative team with proven results.',
    mrr: 32000,
    totalRevenue: 384000,
    cashFlow: 115200,
    location: 'Los Angeles, CA',
    niches: ['Video Production', 'Motion Graphics', 'Tech Companies'],
    employeeCount: 4,
    askingPrice: 1152000,
    type: 'agency',
    featured: true
  },
  // Financial Services AI Businesses
  {
    id: '7',
    description: 'A.I Business implementing fraud detection and risk assessment solutions for major banks and financial institutions. Advanced machine learning algorithms with 99.2% accuracy rate.',
    mrr: 25000,
    totalRevenue: 300000,
    cashFlow: 90000,
    location: 'New York, NY',
    niches: ['Fraud Detection', 'Risk Assessment', 'Financial AI', 'Banking'],
    employeeCount: 4,
    askingPrice: 900000,
    type: 'agency',
    featured: true
  },
  {
    id: '8',
    description: 'A.I Business developing algorithmic trading and portfolio optimization systems for hedge funds and investment firms. Proprietary models delivering consistent alpha generation.',
    mrr: 35000,
    totalRevenue: 420000,
    cashFlow: 126000,
    location: 'Chicago, IL',
    niches: ['Algorithmic Trading', 'Portfolio Optimization', 'Investment AI', 'Hedge Funds'],
    employeeCount: 3,
    askingPrice: 1260000,
    type: 'agency',
    featured: true
  },
  {
    id: '9',
    description: 'A.I Business providing credit scoring and loan underwriting automation for regional banks and credit unions. Reduces processing time by 80% while improving accuracy.',
    mrr: 20000,
    totalRevenue: 240000,
    cashFlow: 72000,
    location: 'Atlanta, GA',
    niches: ['Credit Scoring', 'Loan Underwriting', 'Banking AI', 'Financial Services'],
    employeeCount: 4,
    askingPrice: 720000,
    type: 'agency',
    featured: false
  },
  {
    id: '10',
    description: 'A.I Business specializing in insurance claims processing and fraud prevention for property and casualty insurers. Automated claim assessment with human oversight.',
    mrr: 22000,
    totalRevenue: 264000,
    cashFlow: 79200,
    location: 'Hartford, CT',
    niches: ['Insurance AI', 'Claims Processing', 'Fraud Prevention', 'Property Insurance'],
    employeeCount: 5,
    askingPrice: 792000,
    type: 'agency',
    featured: false
  },
  {
    id: '11',
    description: 'A.I Business building regulatory compliance automation for financial institutions. AI-powered monitoring and reporting for SEC, FINRA, and banking regulations.',
    mrr: 28000,
    totalRevenue: 336000,
    cashFlow: 100800,
    location: 'Washington, DC',
    niches: ['Regulatory Compliance', 'Financial Regulations', 'SEC Compliance', 'Banking'],
    employeeCount: 4,
    askingPrice: 1008000,
    type: 'agency',
    featured: true
  },
  // Medical/Health AI Businesses
  {
    id: '12',
    description: 'A.I Business creating medical imaging solutions for radiology departments. Advanced computer vision for X-ray, MRI, and CT scan analysis with FDA-approved algorithms.',
    mrr: 32000,
    totalRevenue: 384000,
    cashFlow: 115200,
    location: 'Boston, MA',
    niches: ['Medical Imaging', 'Radiology AI', 'Computer Vision', 'Healthcare'],
    employeeCount: 5,
    askingPrice: 1152000,
    type: 'agency',
    featured: true
  },
  {
    id: '13',
    description: 'A.I Business implementing patient scheduling and communication automation for medical practices. AI-powered appointment optimization and patient engagement platform.',
    mrr: 15000,
    totalRevenue: 180000,
    cashFlow: 54000,
    location: 'Phoenix, AZ',
    niches: ['Patient Scheduling', 'Medical Practice AI', 'Healthcare Communication', 'Practice Management'],
    employeeCount: 3,
    askingPrice: 540000,
    type: 'agency',
    featured: false
  },
  {
    id: '14',
    description: 'A.I Business developing clinical decision support systems for hospitals and clinics. AI-driven diagnostic assistance and treatment recommendations based on patient data.',
    mrr: 40000,
    totalRevenue: 480000,
    cashFlow: 144000,
    location: 'Rochester, MN',
    niches: ['Clinical Decision Support', 'Diagnostic AI', 'Hospital Systems', 'Medical AI'],
    employeeCount: 4,
    askingPrice: 1440000,
    type: 'agency',
    featured: true
  },
  {
    id: '15',
    description: 'A.I Business building mental health and therapy analytics platforms. AI-powered mood tracking, therapy session analysis, and treatment outcome prediction.',
    mrr: 18000,
    totalRevenue: 216000,
    cashFlow: 64800,
    location: 'San Diego, CA',
    niches: ['Mental Health AI', 'Therapy Analytics', 'Behavioral Health', 'Psychology'],
    employeeCount: 3,
    askingPrice: 648000,
    type: 'agency',
    featured: false
  },
  {
    id: '16',
    description: 'A.I Business providing pharmaceutical research solutions for drug discovery and development. Machine learning models for compound analysis and clinical trial optimization.',
    mrr: 45000,
    totalRevenue: 540000,
    cashFlow: 162000,
    location: 'Cambridge, MA',
    niches: ['Drug Discovery', 'Pharmaceutical AI', 'Clinical Trials', 'Biotech'],
    employeeCount: 5,
    askingPrice: 1350000,
    type: 'agency',
    featured: true
  },
  {
    id: '17',
    description: 'A.I Business enhancing telemedicine platforms with AI-powered triage and remote monitoring. Improves virtual care delivery and patient outcomes.',
    mrr: 20000,
    totalRevenue: 240000,
    cashFlow: 72000,
    location: 'Nashville, TN',
    niches: ['Telemedicine', 'Remote Monitoring', 'Virtual Care', 'Healthcare AI'],
    employeeCount: 4,
    askingPrice: 720000,
    type: 'agency',
    featured: false
  },
  // Legal AI Businesses
  {
    id: '18',
    description: 'A.I Business implementing contract analysis and review automation for law firms and corporate legal departments. AI-powered clause extraction and risk assessment.',
    mrr: 24000,
    totalRevenue: 288000,
    cashFlow: 86400,
    location: 'Dallas, TX',
    niches: ['Contract Analysis', 'Legal AI', 'Document Review', 'Corporate Law'],
    employeeCount: 4,
    askingPrice: 864000,
    type: 'agency',
    featured: true
  },
  {
    id: '19',
    description: 'A.I Business developing legal research and case law analysis platforms. AI-driven precedent discovery and legal argument generation for litigation support.',
    mrr: 26000,
    totalRevenue: 312000,
    cashFlow: 93600,
    location: 'Seattle, WA',
    niches: ['Legal Research', 'Case Law Analysis', 'Litigation Support', 'Legal AI'],
    employeeCount: 3,
    askingPrice: 936000,
    type: 'agency',
    featured: true
  },
  {
    id: '20',
    description: 'A.I Business creating document discovery and e-discovery automation for large law firms. AI-powered document classification and privilege review.',
    mrr: 30000,
    totalRevenue: 360000,
    cashFlow: 108000,
    location: 'Philadelphia, PA',
    niches: ['E-Discovery', 'Document Review', 'Legal Technology', 'Litigation'],
    employeeCount: 5,
    askingPrice: 1080000,
    type: 'agency',
    featured: false
  },
  {
    id: '21',
    description: 'A.I Business building immigration law automation platforms for immigration attorneys. AI-powered form completion, case tracking, and compliance monitoring.',
    mrr: 16000,
    totalRevenue: 192000,
    cashFlow: 57600,
    location: 'Houston, TX',
    niches: ['Immigration Law', 'Legal Automation', 'Case Management', 'Compliance'],
    employeeCount: 3,
    askingPrice: 576000,
    type: 'agency',
    featured: false
  },
  {
    id: '22',
    description: 'A.I Business specializing in intellectual property and patent analysis for IP law firms and corporations. Prior art search, patent landscape analysis, and infringement detection.',
    mrr: 35000,
    totalRevenue: 420000,
    cashFlow: 126000,
    location: 'San Jose, CA',
    niches: ['Intellectual Property', 'Patent Analysis', 'IP Law', 'Technology Law'],
    employeeCount: 4,
    askingPrice: 1260000,
    type: 'agency',
    featured: true
  },
  // Home Services AI Businesses
  {
    id: '23',
    description: 'A.I Business implementing roofing damage assessment using drone imagery and satellite data. AI-powered roof condition analysis for insurance claims and contractor estimates.',
    mrr: 18000,
    totalRevenue: 216000,
    cashFlow: 64800,
    location: 'Tampa, FL',
    niches: ['Roofing AI', 'Drone Imagery', 'Damage Assessment', 'Insurance Claims'],
    employeeCount: 4,
    askingPrice: 648000,
    type: 'agency',
    featured: true
  },
  {
    id: '24',
    description: 'A.I Business providing lead generation and customer acquisition platforms for home improvement contractors. AI-powered targeting and conversion optimization.',
    mrr: 22000,
    totalRevenue: 264000,
    cashFlow: 79200,
    location: 'Charlotte, NC',
    niches: ['Lead Generation', 'Home Improvement', 'Contractor Marketing', 'Customer Acquisition'],
    employeeCount: 3,
    askingPrice: 792000,
    type: 'agency',
    featured: false
  },
  {
    id: '25',
    description: 'A.I Business developing construction project management and optimization solutions. Predictive scheduling, resource allocation, and cost estimation for residential builders.',
    mrr: 25000,
    totalRevenue: 300000,
    cashFlow: 90000,
    location: 'Denver, CO',
    niches: ['Construction AI', 'Project Management', 'Resource Optimization', 'Residential Building'],
    employeeCount: 5,
    askingPrice: 900000,
    type: 'agency',
    featured: true
  },
  {
    id: '26',
    description: 'A.I Business creating HVAC predictive maintenance and energy optimization platforms. AI-driven system monitoring and efficiency recommendations for residential properties.',
    mrr: 16000,
    totalRevenue: 192000,
    cashFlow: 57600,
    location: 'Kansas City, MO',
    niches: ['HVAC AI', 'Predictive Maintenance', 'Energy Optimization', 'Smart Home'],
    employeeCount: 3,
    askingPrice: 576000,
    type: 'agency',
    featured: false
  },
  {
    id: '27',
    description: 'A.I Business implementing plumbing and electrical service optimization for home service companies. AI-powered route planning, inventory management, and predictive maintenance.',
    mrr: 14000,
    totalRevenue: 168000,
    cashFlow: 50400,
    location: 'Indianapolis, IN',
    niches: ['Plumbing Services', 'Electrical Services', 'Route Optimization', 'Service Management'],
    employeeCount: 2,
    askingPrice: 504000,
    type: 'agency',
    featured: false
  },
  {
    id: '28',
    description: 'A.I Business building solar installation and energy assessment platforms. Automated site analysis, system design, and ROI calculations for residential solar.',
    mrr: 20000,
    totalRevenue: 240000,
    cashFlow: 72000,
    location: 'Sacramento, CA',
    niches: ['Solar Energy', 'Renewable Energy', 'Energy Assessment', 'Green Technology'],
    employeeCount: 4,
    askingPrice: 720000,
    type: 'agency',
    featured: true
  },
  {
    id: '29',
    description: 'A.I Business developing landscaping and lawn care automation platforms. AI-powered scheduling, weather optimization, and customer communication for landscape contractors.',
    mrr: 12000,
    totalRevenue: 144000,
    cashFlow: 43200,
    location: 'Orlando, FL',
    niches: ['Landscaping', 'Lawn Care', 'Outdoor Services', 'Contractor Management'],
    employeeCount: 3,
    askingPrice: 432000,
    type: 'agency',
    featured: false
  },
  {
    id: '30',
    description: 'A.I Business creating home security and smart home integration solutions. Intelligent threat detection, automated responses, and seamless device integration platform.',
    mrr: 28000,
    totalRevenue: 336000,
    cashFlow: 100800,
    location: 'Raleigh, NC',
    niches: ['Home Security', 'Smart Home', 'IoT Integration', 'Security AI'],
    employeeCount: 4,
    askingPrice: 1008000,
    type: 'agency',
    featured: true
  }
];

export default function Portal() {
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>(mockListings);
  const [filteredListings, setFilteredListings] = useState<Listing[]>(mockListings);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);

  // Check authentication on component mount
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('aiexchange_authenticated');
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
    
    const seoData = {
      title: 'AI Business Portal - Browse Verified AI Companies for Sale',
      description: 'Explore our curated collection of verified AI agencies, SaaS platforms, and automation businesses. Find your next acquisition opportunity in the AI industry.',
      keywords: 'AI business portal, AI companies for sale, AI agency acquisition, verified AI businesses, AI marketplace listings, SaaS for sale',
      ogTitle: 'AI Business Portal - Browse Verified AI Companies for Sale',
      ogDescription: 'Explore our curated collection of verified AI agencies, SaaS platforms, and automation businesses. Find your next acquisition opportunity in the AI industry.',
      ogImage: 'https://public.readdy.ai/ai/img_res/864f243fa297423afc7465a6b54dedb4.jpg',
      twitterCard: 'summary_large_image',
      canonicalUrl: `${siteUrl}/portal`,
      structuredData: generateMarketplaceSchema()
    };

    updateSEO(seoData);
  }, []);

  const handleInquiry = (listing: Listing) => {
    setSelectedListing(listing);
    setIsInquiryModalOpen(true);
  };

  const handleFilter = (filters: any) => {
    let filtered = [...listings];

    if (filters.search) {
      filtered = filtered.filter(listing => 
        listing.description.toLowerCase().includes(filters.search.toLowerCase()) ||
        listing.niches.some(niche => niche.toLowerCase().includes(filters.search.toLowerCase())) ||
        listing.location.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.type && filters.type !== 'all') {
      filtered = filtered.filter(listing => listing.type === filters.type);
    }

    if (filters.location && filters.location !== 'all') {
      filtered = filtered.filter(listing => listing.location.includes(filters.location));
    }

    if (filters.mrrRange) {
      const [min, max] = filters.mrrRange;
      filtered = filtered.filter(listing => listing.mrr >= min && listing.mrr <= max);
    }

    if (filters.niches && filters.niches.length > 0) {
      filtered = filtered.filter(listing => 
        filters.niches.some((niche: string) => listing.niches.includes(niche))
      );
    }

    if (filters.featured) {
      filtered = filtered.filter(listing => listing.featured);
    }

    setFilteredListings(filtered);
  };

  // Show loading or redirect if not authenticated
  const isAuthenticated = localStorage.getItem('aiexchange_authenticated');
  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-light mb-4">
                Member <span className="font-semibold">Portal</span>
              </h1>
              <p className="text-gray-400 text-lg">
                Exclusive access to verified agency listings and acquisition opportunities
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-medium">
                <i className="ri-shield-check-line mr-2"></i>
                Verified Member
              </div>
              <div className="text-gray-400 text-sm">
                {filteredListings.length} listings available
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <PortalFilters onFilter={handleFilter} />

        {/* Listings Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {filteredListings.map((listing) => (
            <ListingCard
              key={listing.id}
              listing={listing}
              onInquiry={() => handleInquiry(listing)}
            />
          ))}
        </div>

        {filteredListings.length === 0 && (
          <div className="text-center py-20">
            <i className="ri-search-line text-6xl text-gray-600 mb-6"></i>
            <h3 className="text-2xl font-light text-gray-400 mb-4">No listings found</h3>
            <p className="text-gray-500">Try adjusting your filters to see more results</p>
          </div>
        )}
      </main>

      <Footer />

      {/* Inquiry Modal */}
      {isInquiryModalOpen && selectedListing && (
        <InquiryModal
          listing={selectedListing}
          onClose={() => {
            setIsInquiryModalOpen(false);
            setSelectedListing(null);
          }}
        />
      )}
    </div>
  );
}
